<script setup lang="ts">
import type { HTMLAttributes } from "vue";
import { cn } from "@/lib/utils";

/**
 * An accordion editor section (design skill §6, `acc-sect`): accent-tinted
 * icon + title + chevron, with a dense-field body. Self-contained open state
 * (defaults open); compose <EditorField> inside.
 */
const props = defineProps<{
  title: string;
  icon?: string;
  accent?: string;
  defaultOpen?: boolean;
  class?: HTMLAttributes["class"];
}>();

const open = ref(props.defaultOpen ?? true);
</script>

<template>
  <div
    data-slot="editor-section"
    :class="cn('border-b', props.class)"
  >
    <button
      type="button"
      class="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left transition-colors hover:bg-muted/50"
      :aria-expanded="open"
      @click="open = !open"
    >
      <span
        v-if="props.icon"
        class="flex size-5 items-center justify-center text-muted-foreground"
        :style="props.accent ? { color: `var(--cat-${props.accent})` } : undefined"
      >
        <Icon mode="svg" size="16" :name="props.icon" />
      </span>
      <span class="flex-1 text-[13px] font-semibold text-foreground">{{ props.title }}</span>
      <span v-if="$slots.meta" class="mr-0.5 shrink-0">
        <slot name="meta" />
      </span>
      <Icon mode="svg" size="16"
        name="lucide:chevron-down"
        class="text-muted-foreground transition-transform"
        :class="open && 'rotate-180'"
      />
    </button>
    <div v-show="open" class="space-y-3 px-3 pb-3 pt-1">
      <slot />
    </div>
  </div>
</template>
